import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Pencil, Trash2, ArrowUp, ArrowDown, MapPin, ImageIcon, AlertTriangle, BookOpen } from "lucide-react";
import { useState } from "react";
import { SourceBadge } from "./source-badge";
import { LOCATION_OPTIONS, type TourAudienceCriteria, type TourStepRecord } from "./types";
import { isStepReachable } from "./audience-reach";

interface StepRowProps {
  step: TourStepRecord;
  tourId: string;
  index: number;
  total: number;
  audienceCriteria: TourAudienceCriteria;
  onEdit: (s: TourStepRecord) => void;
}

export function StepRow({ step, tourId, index, total, audienceCriteria, onEdit }: StepRowProps) {
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const reorderMutation = useMutation({
    mutationFn: (direction: "up" | "down") =>
      apiRequest("POST", `/api/admin/tour-steps/${step.id}/reorder`, { direction }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/tours", tourId, "steps"] }),
    onError: (err: Error) => toast({ title: "Reorder failed", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/admin/tour-steps/${step.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/tours", tourId, "steps"] });
      toast({ title: "Step deleted" });
      setConfirmOpen(false);
    },
    onError: (err: Error) => toast({ title: "Delete failed", description: err.message, variant: "destructive" }),
  });

  const locations = step.visibilityCriteria.locations ?? [];
  const roles = step.visibilityCriteria.roles ?? [];
  const bodyPreview = step.body.replace(/\s+/g, " ").slice(0, 120);
  const isChapter = step.stepType === "chapter";
  const isManualSelector = !isChapter && !!step.targetSelector && !step.targetId;
  const reach = isStepReachable(step.visibilityCriteria, audienceCriteria);

  return (
    <div
      className="flex items-start gap-3 rounded-md border p-3"
      data-testid={`row-step-${step.id}`}
    >
      <div className="text-xs font-mono text-muted-foreground pt-1 w-6 shrink-0 text-right">
        {index + 1}.
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium" data-testid={`text-step-title-${step.id}`}>
            {step.title || step.targetId}
          </span>
          {step.targetId && (
            <Badge variant="outline" className="font-mono text-[10px]">
              {step.targetId}
            </Badge>
          )}
          {isChapter && (
            <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`badge-step-chapter-${step.id}`}>
              <BookOpen className="w-3 h-3" /> Chapter
            </Badge>
          )}
          <SourceBadge source={step.source} />
          {step.mediaUrl && (
            <Badge variant="outline" className="gap-1">
              <ImageIcon className="w-3 h-3" /> Image
            </Badge>
          )}
          {isManualSelector && (
            <Badge
              variant="outline"
              className="gap-1 border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
              title="Manual CSS selectors break when the underlying markup changes — prefer data-tour anchors when possible."
              data-testid={`badge-step-fragile-${step.id}`}
            >
              <AlertTriangle className="w-3 h-3" /> Manual selector
            </Badge>
          )}
          {!reach.reachable && (
            <Badge
              variant="outline"
              className="gap-1 border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
              title={reach.reason}
              data-testid={`badge-step-unreachable-${step.id}`}
            >
              <AlertTriangle className="w-3 h-3" /> Unreachable
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">{bodyPreview}</p>
        {(locations.length > 0 || roles.length > 0) && (
          <div className="flex flex-wrap gap-1 pt-1">
            {roles.map((r) => (
              <Badge key={r} variant="secondary" className="text-[10px]">{r}</Badge>
            ))}
            {locations.map((code) => {
              const loc = LOCATION_OPTIONS.find((l) => l.code === code);
              return (
                <Badge key={code} variant="outline" className="gap-1 text-[10px]">
                  <MapPin className="w-3 h-3" />
                  {loc?.label ?? code}
                </Badge>
              );
            })}
          </div>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            data-testid={`button-step-actions-${step.id}`}
            aria-label="Step actions"
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(step)} data-testid={`menu-edit-step-${step.id}`}>
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={index === 0 || reorderMutation.isPending}
            onClick={() => reorderMutation.mutate("up")}
            data-testid={`menu-step-up-${step.id}`}
          >
            <ArrowUp className="w-4 h-4 mr-2" /> Move up
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={index === total - 1 || reorderMutation.isPending}
            onClick={() => reorderMutation.mutate("down")}
            data-testid={`menu-step-down-${step.id}`}
          >
            <ArrowDown className="w-4 h-4 mr-2" /> Move down
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setConfirmOpen(true)}
            className="text-destructive"
            data-testid={`menu-delete-step-${step.id}`}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete step?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this step from the tour. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteMutation.mutate(); }}
              className="bg-destructive text-destructive-foreground"
              data-testid={`button-confirm-delete-step-${step.id}`}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
